import { useNavigate } from 'react-router-dom';
import { useClientTranslation } from '@/lib/core/i18n';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { useExploreCategories, useExploreVenues } from '@/services/platform/data/explore';
import type { VenueGroupVenueDto } from '@/services/platform/api/model';
import { AssistanceBlock } from '../ui/assistance-block';
import { EditorialTitle } from '../ui/editorial-title';
import { EyebrowTags } from '../ui/eyebrow-tags';
import { useVenueBookmarks } from '../ui/useVenueBookmarks';
import { VenueCard } from '../map/venue-card';

/** Matches the backend's `ExploreService.ShopCategorySlug` — Shop venues have no BookableService,
 * so they can't be found via a venue's own `serviceCategoryNodeKey` (always empty for shops).
 * The "Shop" category only exists as its own CMS node, resolved by slug instead. */
const SHOP_CATEGORY_SLUG = 'shop';

interface ShopGroupSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * "View all" for the shop slider — lists every Shop venue across all locations, headed by the
 * Shop category's own editorial title. Mirrors the "no active group" (ungrouped) view of the
 * Explore map's `VenueSheet`.
 */
export function ShopGroupSheet({ open, onOpenChange }: ShopGroupSheetProps) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const { isBookmarked, toggleBookmark } = useVenueBookmarks({ enabled: open });

  // No locationNodeKey on either call — deliberately unscoped from location, all Shop venues.
  const { categories } = useExploreCategories(undefined, { enabled: open });
  const activeCategory = categories.find(c => c.slug === SHOP_CATEGORY_SLUG);
  const { venues: groups } = useExploreVenues(
    { serviceCategoryNodeKey: activeCategory?.nodeKey ?? undefined },
    { enabled: open && !!activeCategory?.nodeKey }
  );

  const categoryTitle = activeCategory?.title ?? '';
  const allVenues = groups.flatMap(g => g.venues ?? []);

  function handleVenueTap(venue: VenueGroupVenueDto) {
    if (!venue.nodeKey) return;
    onOpenChange(false);
    navigate(`/venue-detail/${venue.nodeKey}`, { state: { venue } });
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle>
      <DrawerContent className="max-h-[85vh]">
        <div data-base-ui-swipe-ignore className="flex flex-1 flex-col gap-6 overflow-y-auto pb-10">
          <div className="flex flex-col items-center gap-4 px-4 pt-6 text-center">
            <EyebrowTags labels={[t('App.Explore.Map.All'), activeCategory?.name]} />

            {categoryTitle && <DrawerTitle render={<EditorialTitle text={categoryTitle} />} />}

            {allVenues.length > 0 && (
              <span className="font-pp-neue-montreal text-jncquoi-neutral-800 text-[12px] tracking-[0.5px] uppercase">
                {t('App.Explore.Map.Venues', { count: allVenues.length })}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-4 px-4">
            {allVenues.map(venue => (
              <VenueCard
                key={venue.nodeKey}
                venue={venue}
                isBookmarked={isBookmarked(venue)}
                onBookmark={toggleBookmark}
                onTap={handleVenueTap}
              />
            ))}
            <AssistanceBlock />
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
